import { useQuery } from '@tanstack/react-query';
import { Users, Building2, TrendingUp, AlertCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import type { Client } from '@/types/database';

interface Metrics {
  totalClients: number;
  byStatus: Partial<Record<Client['status'], number>>;
  activeStaff: number;
}

function useGestaoMetrics() {
  return useQuery({
    queryKey: ['gestao_metrics'],
    staleTime: 1000 * 60 * 2,
    queryFn: async (): Promise<Metrics> => {
      const [clientsRes, staffRes] = await Promise.all([
        supabase.from('clients').select('status'),
        supabase.from('staff_clinance').select('id').eq('active', true),
      ]);

      const clients = clientsRes.data ?? [];
      const byStatus = clients.reduce<Partial<Record<Client['status'], number>>>((acc, c) => {
        acc[c.status] = (acc[c.status] ?? 0) + 1;
        return acc;
      }, {});

      return {
        totalClients: clients.length,
        byStatus,
        activeStaff: staffRes.data?.length ?? 0,
      };
    },
  });
}

interface StatCardProps {
  icon: React.ElementType;
  label: string;
  value: number | string;
  sub?: string;
  iconClass?: string;
}

function StatCard({ icon: Icon, label, value, sub, iconClass = 'text-primary' }: StatCardProps) {
  return (
    <div className="rounded-xl border border-border bg-white p-5">
      <div className="mb-3 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary">
          <Icon size={20} className={iconClass} />
        </div>
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
      </div>
      <p className="font-brand text-3xl font-bold text-foreground">{value}</p>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

export function GestaoPage() {
  const { data: metrics, isLoading } = useGestaoMetrics();

  const ativos = metrics?.byStatus['ativo'] ?? 0;
  const onboarding = metrics?.byStatus['onboarding'] ?? 0;
  const leads = metrics?.byStatus['lead'] ?? 0;
  const suspensos = metrics?.byStatus['suspenso'] ?? 0;

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-brand text-2xl font-bold text-foreground">Gestão Geral</h1>
        <p className="text-sm text-muted-foreground">Visão executiva da operação Clinance</p>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-28 animate-pulse rounded-xl border border-border bg-white" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              icon={Building2}
              label="Total de clientes"
              value={metrics?.totalClients ?? 0}
              sub={`${ativos} ativo${ativos !== 1 ? 's' : ''}`}
            />
            <StatCard
              icon={TrendingUp}
              label="MRR"
              value="—"
              sub="Implementado no Sprint 4"
              iconClass="text-accent"
            />
            <StatCard icon={Users} label="Colaboradores ativos" value={metrics?.activeStaff ?? 0} />
            <StatCard
              icon={AlertCircle}
              label="Clientes suspensos"
              value={suspensos}
              iconClass={suspensos > 0 ? 'text-destructive' : 'text-muted-foreground'}
            />
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Leads', value: leads, color: 'bg-blue-100 text-blue-700' },
              { label: 'Em onboarding', value: onboarding, color: 'bg-amber-100 text-amber-700' },
              { label: 'Ativos', value: ativos, color: 'bg-green-100 text-green-700' },
              { label: 'Suspensos', value: suspensos, color: 'bg-red-100 text-red-700' },
            ].map(({ label, value, color }) => (
              <div key={label} className="rounded-xl border border-border bg-white p-4">
                <div
                  className={`mb-2 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${color}`}
                >
                  {label}
                </div>
                <p className="font-brand text-2xl font-bold text-foreground">{value}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 rounded-xl border border-border bg-white p-6">
            <h2 className="mb-3 font-brand text-base font-semibold text-foreground">
              Próximos módulos
            </h2>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>
                • <strong>Sprint 3</strong> — Lançamentos, contas bancárias, fluxo de caixa
              </p>
              <p>
                • <strong>Sprint 4</strong> — DRE, boletos Asaas, convênios, MRR real
              </p>
              <p>
                • <strong>Sprint 5</strong> — Portal do cliente, aprovações, relatórios
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
